import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Building2,
  Globe,
  Briefcase,
  Link as LinkIcon,
  Upload,
  Clock,
  Languages,
  Check,
  ChevronRight,
  ChevronLeft,
  FileText,
  ImageIcon,
  X as XIcon,
} from 'lucide-react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
} from '@mellify/ui';
import { useCreateWorkspace } from './hooks/useCreateWorkspace';
import {
  step1Schema,
  step2Schema,
  generateSlug,
  type Step1Data,
  type Step2Data,
  type CreateWorkspaceFormData,
} from './schemas/createWorkspaceSchema';
import { INDUSTRY_OPTIONS } from './constants/industries';
import { TIMEZONE_OPTIONS } from './constants/timezones';
import type { ZodError } from 'zod';

/* ────────────────────────────────────────────────────────────── */
/*  Types                                                         */
/* ────────────────────────────────────────────────────────────── */

interface CreateWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
}

type StepErrors = Record<string, string>;

const STEPS = ['Basic Info', 'Branding', 'Review'] as const;
const TOTAL_STEPS = STEPS.length;

/* ────────────────────────────────────────────────────────────── */
/*  Helper: parse Zod errors into a flat record                   */
/* ────────────────────────────────────────────────────────────── */

function flattenZodErrors(error: ZodError): StepErrors {
  const errors: StepErrors = {};
  for (const issue of error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) {
      errors[key] = issue.message;
    }
  }
  return errors;
}

/* ────────────────────────────────────────────────────────────── */
/*  Step Indicator                                                */
/* ────────────────────────────────────────────────────────────── */

const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  const progressPercent = ((currentStep + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="px-6 pt-4 pb-2">
      {/* Progress bar */}
      <div className="w-full h-1.5 bg-dark-200 dark:bg-dark-700 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-primary-500 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Step labels */}
      <div className="flex items-center justify-between">
        {STEPS.map((label, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;

          return (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`
                  w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors
                  ${
                    isCompleted
                      ? 'bg-primary-500 text-white'
                      : isCurrent
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 ring-2 ring-primary-500'
                        : 'bg-dark-100 dark:bg-dark-700 text-dark-400 dark:text-dark-500'
                  }
                `}
              >
                {isCompleted ? <Check className="w-3.5 h-3.5" /> : index + 1}
              </div>
              <span
                className={`text-xs font-medium hidden sm:inline ${
                  isCurrent
                    ? 'text-dark-900 dark:text-dark-100'
                    : 'text-dark-400 dark:text-dark-500'
                }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────── */
/*  Step 1 – Basic Info                                           */
/* ────────────────────────────────────────────────────────────── */

interface Step1Props {
  data: Step1Data;
  errors: StepErrors;
  onChange: (field: keyof Step1Data, value: string) => void;
  slugEdited: boolean;
  onSlugEdit: () => void;
}

const Step1BasicInfo: React.FC<Step1Props> = ({
  data,
  errors,
  onChange,
  slugEdited,
  onSlugEdit,
}) => {
  return (
    <div className="space-y-5">
      {/* Name */}
      <Input
        label="Workspace Name"
        placeholder="e.g. Acme Inc."
        value={data.name}
        onChange={(e) => onChange('name', e.target.value)}
        error={errors.name}
        leftIcon={<Building2 className="w-4 h-4" />}
      />

      {/* Slug */}
      <div>
        <Input
          label="URL Slug"
          placeholder="acme-inc"
          value={data.slug}
          onChange={(e) => {
            onSlugEdit();
            onChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
          }}
          error={errors.slug}
          leftIcon={<LinkIcon className="w-4 h-4" />}
          helperText={
            !slugEdited
              ? 'Auto-generated from workspace name. Click to edit.'
              : 'Lowercase letters, numbers, and hyphens only.'
          }
        />
      </div>

      {/* Description */}
      <Input
        label="Description"
        placeholder="Briefly describe your workspace (optional)"
        value={data.description || ''}
        onChange={(e) => onChange('description', e.target.value)}
        error={errors.description}
        leftIcon={<FileText className="w-4 h-4" />}
      />

      {/* Industry */}
      <Select
        label="Industry"
        placeholder="Select an industry"
        value={data.industry || ''}
        onChange={(e) => onChange('industry', e.target.value)}
        error={errors.industry}
        options={[{ value: '', label: 'Select an industry (optional)' }, ...INDUSTRY_OPTIONS]}
      />

      {/* Website */}
      <Input
        label="Website"
        placeholder="https://example.com"
        value={data.website || ''}
        onChange={(e) => onChange('website', e.target.value)}
        error={errors.website}
        leftIcon={<Globe className="w-4 h-4" />}
        helperText="Optional"
      />
    </div>
  );
};

/* ────────────────────────────────────────────────────────────── */
/*  Step 2 – Branding & Settings                                  */
/* ────────────────────────────────────────────────────────────── */

interface Step2Props {
  data: Step2Data;
  errors: StepErrors;
  onLogoChange: (file: File | null, preview: string | null) => void;
  onFieldChange: (field: keyof Step2Data, value: string) => void;
}

const Step2Branding: React.FC<Step2Props> = ({ data, errors, onLogoChange, onFieldChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) return; // 5MB limit
      const reader = new FileReader();
      reader.onload = () => {
        onLogoChange(file, reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [onLogoChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div className="space-y-5">
      {/* Logo upload */}
      <div>
        <label className="block text-sm font-medium text-dark-700 dark:text-dark-200 mb-1.5">
          Workspace Logo
        </label>
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${
              isDragging
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-dark-300 dark:border-dark-600 hover:border-primary-400 dark:hover:border-primary-600'
            }
          `}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {data.logoPreview ? (
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <img
                  src={data.logoPreview}
                  alt="Logo preview"
                  className="w-20 h-20 rounded-xl object-cover border border-dark-200 dark:border-dark-700"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLogoChange(null, null);
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-danger-500 text-white flex items-center justify-center hover:bg-danger-600 transition-colors"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="text-xs text-dark-500 dark:text-dark-400">
                Click or drag to replace
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-dark-100 dark:bg-dark-700 flex items-center justify-center">
                <Upload className="w-5 h-5 text-dark-400 dark:text-dark-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-dark-700 dark:text-dark-300">
                  Drag & drop your logo here
                </p>
                <p className="text-xs text-dark-400 dark:text-dark-500 mt-0.5">
                  PNG, JPG, or SVG up to 5MB
                </p>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* Timezone */}
      <Select
        label="Timezone"
        value={data.timezone}
        onChange={(e) => onFieldChange('timezone', e.target.value)}
        error={errors.timezone}
        options={[...TIMEZONE_OPTIONS]}
      />

      {/* Default Language */}
      <Select
        label="Default Language"
        value={data.defaultLanguage}
        onChange={(e) => onFieldChange('defaultLanguage', e.target.value)}
        error={errors.defaultLanguage}
        options={[
          { value: 'en', label: 'English' },
          { value: 'es', label: 'Spanish' },
          { value: 'fr', label: 'French' },
          { value: 'de', label: 'German' },
          { value: 'pt', label: 'Portuguese' },
          { value: 'it', label: 'Italian' },
          { value: 'nl', label: 'Dutch' },
          { value: 'ja', label: 'Japanese' },
          { value: 'ko', label: 'Korean' },
          { value: 'zh', label: 'Chinese' },
          { value: 'ar', label: 'Arabic' },
          { value: 'hi', label: 'Hindi' },
        ]}
      />
    </div>
  );
};

/* ────────────────────────────────────────────────────────────── */
/*  Step 3 – Review & Create                                      */
/* ────────────────────────────────────────────────────────────── */

interface Step3Props {
  data: CreateWorkspaceFormData;
}

const Step3Review: React.FC<Step3Props> = ({ data }) => {
  const industryLabel = INDUSTRY_OPTIONS.find((i) => i.value === data.industry)?.label;
  const timezoneLabel = TIMEZONE_OPTIONS.find((t) => t.value === data.timezone)?.label;

  return (
    <div className="space-y-4">
      <p className="text-sm text-dark-500 dark:text-dark-400">
        Please review your workspace details before creating.
      </p>

      <div className="rounded-lg border border-dark-200 dark:border-dark-700 divide-y divide-dark-200 dark:divide-dark-700">
        {/* Logo */}
        {data.logoPreview && (
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="flex items-center gap-2 w-32 shrink-0">
              <ImageIcon className="w-4 h-4 text-dark-400" />
              <span className="text-sm font-medium text-dark-600 dark:text-dark-300">Logo</span>
            </div>
            <img
              src={data.logoPreview}
              alt="Workspace logo"
              className="w-10 h-10 rounded-lg object-cover border border-dark-200 dark:border-dark-700"
            />
          </div>
        )}

        {/* Name */}
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2 w-32 shrink-0">
            <Building2 className="w-4 h-4 text-dark-400" />
            <span className="text-sm font-medium text-dark-600 dark:text-dark-300">Name</span>
          </div>
          <span className="text-sm text-dark-900 dark:text-dark-100">{data.name}</span>
        </div>

        {/* Slug */}
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2 w-32 shrink-0">
            <LinkIcon className="w-4 h-4 text-dark-400" />
            <span className="text-sm font-medium text-dark-600 dark:text-dark-300">Slug</span>
          </div>
          <span className="text-sm text-dark-900 dark:text-dark-100 font-mono">{data.slug}</span>
        </div>

        {/* Description */}
        {data.description && (
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="flex items-center gap-2 w-32 shrink-0">
              <FileText className="w-4 h-4 text-dark-400" />
              <span className="text-sm font-medium text-dark-600 dark:text-dark-300">
                Description
              </span>
            </div>
            <span className="text-sm text-dark-900 dark:text-dark-100">{data.description}</span>
          </div>
        )}

        {/* Industry */}
        {industryLabel && (
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="flex items-center gap-2 w-32 shrink-0">
              <Briefcase className="w-4 h-4 text-dark-400" />
              <span className="text-sm font-medium text-dark-600 dark:text-dark-300">
                Industry
              </span>
            </div>
            <span className="text-sm text-dark-900 dark:text-dark-100">{industryLabel}</span>
          </div>
        )}

        {/* Website */}
        {data.website && (
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="flex items-center gap-2 w-32 shrink-0">
              <Globe className="w-4 h-4 text-dark-400" />
              <span className="text-sm font-medium text-dark-600 dark:text-dark-300">Website</span>
            </div>
            <span className="text-sm text-primary-600 dark:text-primary-400">{data.website}</span>
          </div>
        )}

        {/* Timezone */}
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2 w-32 shrink-0">
            <Clock className="w-4 h-4 text-dark-400" />
            <span className="text-sm font-medium text-dark-600 dark:text-dark-300">Timezone</span>
          </div>
          <span className="text-sm text-dark-900 dark:text-dark-100">
            {timezoneLabel || data.timezone}
          </span>
        </div>

        {/* Language */}
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2 w-32 shrink-0">
            <Languages className="w-4 h-4 text-dark-400" />
            <span className="text-sm font-medium text-dark-600 dark:text-dark-300">Language</span>
          </div>
          <span className="text-sm text-dark-900 dark:text-dark-100 uppercase">
            {data.defaultLanguage}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────── */
/*  CreateWorkspaceModal                                          */
/* ────────────────────────────────────────────────────────────── */

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({ open, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<StepErrors>({});
  const [slugEdited, setSlugEdited] = useState(false);

  // Step 1 data
  const [step1Data, setStep1Data] = useState<Step1Data>({
    name: '',
    slug: '',
    description: '',
    industry: '',
    website: '',
  });

  // Step 2 data
  const [step2Data, setStep2Data] = useState<Step2Data>({
    logoFile: null,
    logoPreview: null,
    timezone: 'UTC',
    defaultLanguage: 'en',
  });

  const createWorkspace = useCreateWorkspace();

  // Reset on close
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setErrors({});
      setSlugEdited(false);
      setStep1Data({ name: '', slug: '', description: '', industry: '', website: '' });
      setStep2Data({ logoFile: null, logoPreview: null, timezone: 'UTC', defaultLanguage: 'en' });
    }
  }, [open]);

  /* ── Step 1 handlers ───────────────────────────────────────── */

  const handleStep1Change = useCallback(
    (field: keyof Step1Data, value: string) => {
      setStep1Data((prev) => {
        const next = { ...prev, [field]: value };

        // Auto-generate slug from name if not manually edited
        if (field === 'name' && !slugEdited) {
          next.slug = generateSlug(value);
        }

        return next;
      });

      // Clear field error on change
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [slugEdited, errors]
  );

  /* ── Step 2 handlers ───────────────────────────────────────── */

  const handleLogoChange = useCallback((file: File | null, preview: string | null) => {
    setStep2Data((prev) => ({ ...prev, logoFile: file, logoPreview: preview }));
  }, []);

  const handleStep2FieldChange = useCallback(
    (field: keyof Step2Data, value: string) => {
      setStep2Data((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [errors]
  );

  /* ── Validation ────────────────────────────────────────────── */

  const validateCurrentStep = (): boolean => {
    try {
      if (currentStep === 0) {
        step1Schema.parse(step1Data);
      } else if (currentStep === 1) {
        step2Schema.parse(step2Data);
      }
      setErrors({});
      return true;
    } catch (error) {
      const zodError = error as ZodError;
      setErrors(flattenZodErrors(zodError));
      return false;
    }
  };

  /* ── Navigation ────────────────────────────────────────────── */

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  /* ── Submit ────────────────────────────────────────────────── */

  const handleCreate = () => {
    createWorkspace.mutate(
      {
        data: {
          name: step1Data.name,
          slug: step1Data.slug,
          description: step1Data.description || undefined,
          industry: step1Data.industry || undefined,
          website: step1Data.website || undefined,
          timezone: step2Data.timezone,
          defaultLanguage: step2Data.defaultLanguage,
        },
        logoFile: step2Data.logoFile,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  /* ── Combined form data for review ─────────────────────────── */

  const allData: CreateWorkspaceFormData = { ...step1Data, ...step2Data };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      closeOnOverlayClick={!createWorkspace.isPending}
      closeOnEscape={!createWorkspace.isPending}
      aria-label="Create workspace"
    >
      <ModalHeader>
        <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-100">
          Create Workspace
        </h2>
      </ModalHeader>

      <StepIndicator currentStep={currentStep} />

      <ModalBody>
        {currentStep === 0 && (
          <Step1BasicInfo
            data={step1Data}
            errors={errors}
            onChange={handleStep1Change}
            slugEdited={slugEdited}
            onSlugEdit={() => setSlugEdited(true)}
          />
        )}

        {currentStep === 1 && (
          <Step2Branding
            data={step2Data}
            errors={errors}
            onLogoChange={handleLogoChange}
            onFieldChange={handleStep2FieldChange}
          />
        )}

        {currentStep === 2 && <Step3Review data={allData} />}
      </ModalBody>

      <ModalFooter>
        {currentStep > 0 && (
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={createWorkspace.isPending}
            leftIcon={<ChevronLeft className="w-4 h-4" />}
          >
            Back
          </Button>
        )}

        <div className="flex-1" />

        {currentStep < TOTAL_STEPS - 1 && (
          <Button
            variant="primary"
            onClick={handleNext}
            rightIcon={<ChevronRight className="w-4 h-4" />}
          >
            Next
          </Button>
        )}

        {currentStep === TOTAL_STEPS - 1 && (
          <Button
            variant="primary"
            onClick={handleCreate}
            loading={createWorkspace.isPending}
          >
            Create Workspace
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CreateWorkspaceModal;